/**
 * Geometry utilities for radar calculations
 * Phase 1: Foundation & Architecture
 */

export interface Point {
  x: number
  y: number
}

export interface PolarCoordinate {
  angle: number // degrees, -90 = top
  distance: number
}

/**
 * Convert polar coordinates to cartesian
 * @param angle - Angle in degrees (-90 = top, 0 = right)
 * @param distance - Distance from center
 * @param centerX - Center X coordinate
 * @param centerY - Center Y coordinate
 */
export function polarToCartesian(
  angle: number,
  distance: number,
  centerX: number,
  centerY: number
): Point {
  // Convert angle to radians
  // -90 degrees offset because 0 degrees is right, but we want -90 to be top
  const radians = ((angle - 90) * Math.PI) / 180
  
  return {
    x: centerX + distance * Math.cos(radians),
    y: centerY + distance * Math.sin(radians),
  }
}

/**
 * Convert cartesian coordinates to polar
 * @param x - X coordinate
 * @param y - Y coordinate
 * @param centerX - Center X coordinate
 * @param centerY - Center Y coordinate
 */
export function cartesianToPolar(
  x: number,
  y: number,
  centerX: number,
  centerY: number
): PolarCoordinate {
  const dx = x - centerX
  const dy = y - centerY
  
  const distance = Math.sqrt(dx * dx + dy * dy)
  let angle = (Math.atan2(dy, dx) * 180) / Math.PI
  
  // Adjust angle so -90 is top
  angle += 90
  
  // Normalize to -180 to 180
  if (angle > 180) angle -= 360
  if (angle < -180) angle += 360
  
  return { angle, distance }
}

/**
 * Calculate distance between two points
 */
export function distanceBetween(p1: Point, p2: Point): number {
  const dx = p1.x - p2.x
  const dy = p1.y - p2.y
  return Math.sqrt(dx * dx + dy * dy)
}

/**
 * Check if a point is within a circle
 */
export function isPointInCircle(
  point: Point,
  circleCenter: Point,
  radius: number
): boolean {
  return distanceBetween(point, circleCenter) <= radius
}

/**
 * Calculate zone radius based on radar configuration
 */
export function calculateZoneRadius(
  zone: 'panic_zone' | 'middle_zone' | 'outer_zone',
  maxRadius: number
): number {
  switch (zone) {
    case 'panic_zone':
      return maxRadius * 0.2
    case 'middle_zone':
      return maxRadius * 0.5
    case 'outer_zone':
    default:
      return maxRadius
  }
}

/**
 * Calculate bubble radial position based on urgency
 */
export function calculateBubbleRadialDistance(
  hoursUntilLesson: number,
  distanceFromCenter: number,
  maxRadius: number,
  panicThreshold: number = 2,
  middleThreshold: number = 48,
  isResolved: boolean = false
): number {
  if (isResolved) {
    // Resolved bubbles go to inner ring
    return maxRadius * 0.1 * (1 - distanceFromCenter * 0.5)
  }
  
  if (hoursUntilLesson <= panicThreshold) {
    // Panic zone: very close to center
    return maxRadius * 0.05 + distanceFromCenter * maxRadius * 0.15
  } else if (hoursUntilLesson <= middleThreshold) {
    // Middle zone
    return maxRadius * 0.2 + distanceFromCenter * maxRadius * 0.3
  } else {
    // Outer zone
    return maxRadius * 0.5 + distanceFromCenter * maxRadius * 0.5
  }
}

/**
 * Calculate cluster offset angle
 */
export function calculateClusterOffset(
  clusterIndex: number,
  baseAngle: number,
  offsetAngle: number = 15
): number {
  const direction = clusterIndex % 2 === 0 ? 1 : -1
  const magnitude = Math.floor((clusterIndex + 1) / 2)
  return baseAngle + direction * offsetAngle * magnitude
}

/**
 * Create Bezier curve control point for organic rays
 */
export function calculateBezierControlPoint(
  start: Point,
  end: Point,
  curveAmount: number = 0.2
): Point {
  const midX = (start.x + end.x) / 2
  const midY = (start.y + end.y) / 2
  
  // Perpendicular offset
  const dx = end.x - start.x
  const dy = end.y - start.y
  const dist = Math.sqrt(dx * dx + dy * dy)
  
  // Normalize and rotate 90 degrees
  const perpX = -dy / dist
  const perpY = dx / dist
  
  return {
    x: midX + perpX * dist * curveAmount,
    y: midY + perpY * dist * curveAmount,
  }
}

/**
 * Get bounding box for collision detection
 */
export function getBoundingBox(
  center: Point,
  radius: number
): { left: number; top: number; right: number; bottom: number } {
  return {
    left: center.x - radius,
    top: center.y - radius,
    right: center.x + radius,
    bottom: center.y + radius,
  }
}

/**
 * Check if two bounding boxes overlap
 */
export function doBoundingBoxesOverlap(
  bb1: { left: number; top: number; right: number; bottom: number },
  bb2: { left: number; top: number; right: number; bottom: number }
): boolean {
  return !(
    bb1.right < bb2.left ||
    bb1.left > bb2.right ||
    bb1.bottom < bb2.top ||
    bb1.top > bb2.bottom
  )
}
